<template>
    <div class="ui-page">
        <header class="ui-page-header">
            <div class="ui-page-header__row">
                <div>
                    <h1 class="ui-title">Adesão ao Plano</h1>
                    <p class="ui-subtitle">Uma campanha por caso: plano, termo, adesões dos credores e quórum.</p>
                </div>
                <v-btn v-if="$allowed('create')" color="primary" depressed @click="openCreate"><v-icon left small>mdi-plus</v-icon>Nova campanha</v-btn>
            </div>
        </header>

        <div class="ui-list-bar">
            <ui-pills v-model="statusTab" :items="pillItems" />
        </div>

        <div class="ui-table-card">
            <div class="ui-toolbar">
                <v-text-field v-model="filter.search" placeholder="Buscar pelo nome do caso ou número do processo" dense outlined hide-details
                    prepend-inner-icon="mdi-magnify" class="ui-toolbar__search"></v-text-field>
                <v-btn text class="ui-toolbar__clear" :disabled="!filter.search && statusTab === 'all'" @click="clearFilters"><v-icon left small>mdi-restore</v-icon>Limpar</v-btn>
            </div>
            <v-data-table :headers="headers" :items="visibleItems" :loading="loading" disable-sort hide-default-footer
                :page.sync="page" :items-per-page="itemsPerPage"
                no-data-text="Nenhuma campanha encontrada" loading-text="Carregando..." class="ui-clickable-rows" @click:row="open">
                <template v-slot:[`item.client_name`]="{ item }">
                    <div class="ui-cell-main">{{ item.client_name }}</div>
                    <div class="ui-cell-sub">{{ item.process_number || "—" }}</div>
                </template>
                <template v-slot:[`item.status`]="{ item }"><ui-badge set="adhesion" :status="item.status" /></template>
                <template v-slot:[`item.window`]="{ item }">
                    <template v-if="item.opens_at">{{ formatDateTime(item.opens_at) }} → {{ item.closes_at ? formatDateTime(item.closes_at) : "sem encerramento" }}</template>
                    <span v-else class="ui-muted">Não configurada</span>
                </template>
                <template v-slot:[`item.actions`]><v-icon small class="ui-cell-chevron">mdi-chevron-right</v-icon></template>
            </v-data-table>
            <ui-pagination :total="visibleItems.length" :page.sync="page" :items-per-page.sync="itemsPerPage" />
        </div>

        <v-dialog v-model="create.open" max-width="520">
            <v-card class="ui-card">
                <div class="ui-card__header">
                    <div>
                        <h3 class="ui-section-title">Nova campanha de adesão</h3>
                        <p class="ui-section-description">Depois de criar, envie o plano, publique o termo e abra a adesão.</p>
                    </div>
                </div>
                <div class="ui-card__body">
                    <v-autocomplete v-model="create.client_id" :items="clients" item-value="id" item-text="name" label="Caso"
                        outlined dense hide-details no-data-text="Nenhum caso disponível"></v-autocomplete>
                </div>
                <div class="ui-card__footer">
                    <v-btn outlined @click="create.open = false">Cancelar</v-btn>
                    <v-btn v-if="$allowed('create')" color="primary" depressed :disabled="!create.client_id" :loading="create.loading" @click="confirmCreate">Criar campanha</v-btn>
                </div>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import axios from "axios";
import { errorMessage, formatDateTime } from "@/components/plan-adhesion/plan-adhesion.util";

const STATUS_TABS = [
    { value: "all", text: "Todas" },
    { value: "open", text: "Abertas", tone: "success" },
    { value: "draft", text: "Rascunho", tone: "neutral" },
    { value: "closed", text: "Encerradas", tone: "neutral" },
];
import "@/components/plan-adhesion/plan-adhesion.scss";

export default {
    name: "PlanAdhesionList",


    data: () => ({
        items: [],
        loading: false,
        clients: [],
        filter: { search: "" },
        statusTab: "all",
        page: 1,
        itemsPerPage: 10,
        searchTimer: null,
        create: { open: false, client_id: null, loading: false },
        headers: [
            { text: "Caso", value: "client_name" },
            { text: "Status", value: "status" },
            { text: "Janela de adesão", value: "window", cellClass: "ui-nowrap" },
            { text: "", value: "actions", align: "end", width: 32 },
        ],
    }),

    computed: {
        pillItems() {
            return STATUS_TABS.map((tab) => ({ ...tab, count: tab.value === "all" ? this.items.length : this.items.filter((item) => item.status === tab.value).length }));
        },
        visibleItems() {
            return this.statusTab === "all" ? this.items : this.items.filter((item) => item.status === this.statusTab);
        },
    },

    watch: {
        statusTab() {
            this.page = 1;
        },
        "filter.search"() {
            clearTimeout(this.searchTimer);
            this.searchTimer = setTimeout(() => this.load(), 400);
        },
    },

    created() {
        this.load();
    },

    beforeDestroy() {
        clearTimeout(this.searchTimer);
    },

    methods: {
        formatDateTime,

        clearFilters() {
            this.filter.search = "";
            this.statusTab = "all";
        },

        async load() {
            this.loading = true;
            try {
                const params = this.filter.search ? { search: this.filter.search } : {};
                const result = await axios.get("/api/admin/plan-adhesions", { params });
                this.items = result.data.content;
                this.page = 1;
            } catch (error) {
                this.$dialog.notify.error(errorMessage(error), { position: "top-right", timeout: 4000 });
            } finally {
                this.loading = false;
            }
        },

        async openCreate() {
            this.create = { open: true, client_id: null, loading: false };
            if (this.clients.length) return;
            try {
                const result = await axios.get("/api/clients", { params: { start: 1, limit: 100000, search: {} } });
                const used = new Set(this.items.map((item) => Number(item.client_id)));
                this.clients = result.data.content.data
                    .filter((client) => !used.has(Number(client.id)))
                    .sort((a, b) => a.name.localeCompare(b.name));
            } catch (error) {
                this.$dialog.notify.error(errorMessage(error), { position: "top-right", timeout: 4000 });
            }
        },

        async confirmCreate() {
            this.create.loading = true;
            try {
                const result = await axios.post("/api/admin/plan-adhesions", { client_id: this.create.client_id });
                this.$router.push(`/adesao-plano/${result.data.content.id}`);
            } catch (error) {
                this.$dialog.notify.error(errorMessage(error), { position: "top-right", timeout: 4000 });
            } finally {
                this.create.loading = false;
            }
        },

        open(item) {
            this.$router.push(`/adesao-plano/${item.id}`);
        },
    },
};
</script>
